import React, { useState, useEffect, useRef } from 'react';
import { Eye, X, ChevronDown, User as UserIcon, Users } from 'lucide-react';
import { useRolePreview } from '../context/RolePreviewContext';
import PreviewAsClientModal from './PreviewAsClientModal';
import PreviewAsStaffModal from './PreviewAsStaffModal';

/**
 * <RolePreviewSwitcher /> — small luxury chrome button that lets an admin
 * preview the staff or vendor experience without logging out.
 *
 * Renders nothing for non-admin users (the context's `isPreviewing` flag
 * stays false because buildEffectiveUser ignores the override for them).
 */
export default function RolePreviewSwitcher({ realUser }) {
  const { previewRole, setPreviewRole, previewStaffName, setPreviewStaff, isPreviewing } = useRolePreview();
  const [open, setOpen] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);
  const wrapRef = useRef(null);
  // Close the menu on any outside click or Escape. We use a document-level
  // listener instead of a fixed full-screen backdrop because the app header
  // uses backdrop-blur, which traps position:fixed children inside the header
  // box — so a fixed backdrop only covers the header, not the page body.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Hide entirely for staff/vendor — they don't get this control.
  if (!realUser) return null;
  const isUnderlyingAdmin = !(
    realUser.role === 'staff' || realUser.industryType === 'staff' || realUser.isStaff ||
    realUser.role === 'vendor' || realUser.industryType === 'vendor' || realUser.isVendor
  );
  if (!isUnderlyingAdmin) return null;

  // Block 56 moved staff preview onto applyStaffPerson (a role is not a
  // person), which left `apply` reachable only with null. A branch nothing
  // can call is a branch nobody maintains - so this is now what it actually
  // is: the single place that clears a preview and returns to admin.
  const exitPreview = () => {
    setPreviewRole(null);
    setOpen(false);
    window.location.assign('/control-center');
  };
  // Picking a PERSON lands on My Assignments, not the portal home: that is the
  // screen wired to the preview endpoint, so it is the one that shows their
  // real rows. Sending them to /staff-portal would still render an empty page
  // and reproduce the exact confusion this replaces.
  const applyStaffPerson = (staffId, staffName) => {
    setPreviewStaff(staffId, staffName);
    setPreviewRole('staff');
    setStaffPickerOpen(false);
    setOpen(false);
    window.location.assign('/staff/my-assignments');
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wide transition-all hover:-translate-y-px"
        style={{
          background: isPreviewing
            ? 'linear-gradient(135deg, rgba(168,184,255,0.18), rgba(168,184,255,0.06))'
            : 'rgba(255,255,255,0.04)',
          border: `1px solid ${isPreviewing ? 'rgba(168,184,255,0.42)' : 'rgba(255,255,255,0.10)'}`,
          color: isPreviewing ? '#A8B8FF' : 'rgba(255,255,255,0.55)',
        }}
        data-testid="role-preview-switcher-btn"
        title="Preview the staff or vendor view as an admin"
      >
        <Eye className="w-3 h-3" />
        {isPreviewing ? `Previewing as ${previewRole}` : 'Preview as'}
        <ChevronDown className="w-2.5 h-2.5 opacity-60" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-xl shadow-2xl z-50 overflow-hidden"
          style={{ background: 'rgba(15, 15, 26, 0.96)', border: '1px solid rgba(212,175,55,0.18)', backdropFilter: 'blur(12px)' }}
          data-testid="role-preview-switcher-menu"
        >
          <button onClick={() => { setOpen(false); setStaffPickerOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/[0.04] transition text-left"
            data-testid="role-preview-switcher-staff">
            <UserIcon className="w-3.5 h-3.5 text-[#34D399]" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-white/85">Preview as Staff</p>
              <p className="text-[9.5px] text-white/40 mt-0.5">Pick a crew member to see their view</p>
            </div>
            {previewRole === 'staff' && <span className="text-[9px] text-[#A8B8FF] font-bold">ACTIVE</span>}
          </button>
          <button
            onClick={() => { setOpen(false); setClientPickerOpen(true); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/[0.04] transition text-left border-t border-white/[0.04]"
            data-testid="role-preview-switcher-client"
          >
            <Users className="w-3.5 h-3.5 text-[#D4AF37]" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-white/85">Preview as Client</p>
              <p className="text-[9.5px] text-white/40 mt-0.5">Open a client portal for an event</p>
            </div>
          </button>
          {isPreviewing && (
            <button onClick={exitPreview} className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-red-500/10 transition text-left border-t border-white/[0.04]"
              data-testid="role-preview-switcher-exit">
              <X className="w-3.5 h-3.5 text-[#F87171]" />
              <p className="text-[11px] font-semibold text-[#FCA5A5]">Exit Preview</p>
            </button>
          )}
        </div>
      )}
      <PreviewAsClientModal open={clientPickerOpen} onClose={() => setClientPickerOpen(false)} />
      <PreviewAsStaffModal
        open={staffPickerOpen}
        onClose={() => setStaffPickerOpen(false)}
        onPick={applyStaffPerson}
      />
    </div>
  );
}

/**
 * Floating banner that appears at the top of any page while a preview is
 * active. Sticky so the admin always sees a clear indicator + one-click exit.
 */
export function RolePreviewBanner() {
  const { isPreviewing, previewRole, previewStaffName, setPreviewRole } = useRolePreview();
  if (!isPreviewing) return null;
  const exit = () => {
    setPreviewRole(null);
    window.location.assign('/control-center');
  };
  return (
    <div
      className="sticky top-0 z-[60] flex items-center justify-center gap-3 px-4 py-2 text-[12px] font-semibold"
      style={{
        background: 'linear-gradient(90deg, rgba(168,184,255,0.18), rgba(212,175,55,0.18))',
        borderBottom: '1px solid rgba(168,184,255,0.32)',
        color: '#FFFFFF',
      }}
      data-testid="role-preview-banner"
    >
      <Eye className="w-3.5 h-3.5 text-[#A8B8FF]" />
      <span>Previewing as <span className="font-bold text-[#A8B8FF]">{previewStaffName || previewRole}</span> · read-only · admin controls hidden</span>
      <button
        onClick={exit}
        className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase"
        style={{ background: 'rgba(0,0,0,0.32)', color: '#FFFFFF' }}
        data-testid="role-preview-banner-exit"
      >
        <X className="w-2.5 h-2.5" /> Exit
      </button>
    </div>
  );
}
